export const MAX_ITERATIONS = 1024;
export const SMOOTHING_STEPS = 3;
const LOG2_INV = 1 / Math.log(2);
const STRIPE_BAILOUT = 10000;
const LOG_STRIPE_BAILOUT = 0.5 * Math.log(STRIPE_BAILOUT);
const ORBIT_TRAP_BAILOUT = 256;
const LOG_ORBIT_TRAP_BAILOUT = 0.5 * Math.log(ORBIT_TRAP_BAILOUT);
const STRIPE_FREQUENCY = 6;

export const PATTERN_MODES = new Set([
  'stripe',
  'binary_cells',
  'normal_3d',
  'pickover_stalks',
  'cellular_web',
  'equipotential_rings',
]);

export function isPatternMode(mode) {
  return typeof mode === 'string' && PATTERN_MODES.has(mode);
}

function isCardioidOrBulb(x0, y0) {
  const xm = x0 - 0.25;
  const y20 = y0 * y0;
  const q = xm * xm + y20;
  const xp1 = x0 + 1;
  return q * (q + xm) <= 0.25 * y20 || xp1 * xp1 + y20 <= 0.0625;
}

function finalizeEscape(result, iteration, x, y, x2, y2, x0, y0, useMu) {
  result.iterations = iteration;
  result.escaped = x2 + y2 > 4;
  result.mu = 0;
  if (result.escaped && useMu) {
    // Refine the escaped orbit before evaluating the continuous count. At
    // radius 2 alone, the approximation still produces visible contour seams.
    for (let i = 0; i < SMOOTHING_STEPS; i++) {
      y = 2 * x * y + y0;
      x = x2 - y2 + x0;
      x2 = x * x;
      y2 = y * y;
    }
    result.mu = iteration + SMOOTHING_STEPS + 1 - Math.log(0.5 * Math.log(x2 + y2)) * LOG2_INV;
  }
  return result;
}

// Shared by the main thread and workers. Keep the shader's equivalent in sync.
// A budget of N permits N recurrence steps and checks z_N for strict escape.
// The separate flag matters when escape occurs on the last permitted step.
export function iterateUntilEscape(x0, y0, result, maxIterations = MAX_ITERATIONS, useMu = true) {
  // Skip the known interior of the main cardioid and period-2 bulb. This keeps
  // the increased boundary detail budget inexpensive in overview images.
  if (isCardioidOrBulb(x0, y0)) {
    result.iterations = maxIterations;
    result.escaped = false;
    result.mu = 0;
    return result;
  }
  let x = 0, y = 0, x2 = 0, y2 = 0, iteration = 0;
  while (x2 + y2 <= 4 && iteration < maxIterations) {
    y = 2 * x * y + y0;
    x = x2 - y2 + x0;
    x2 = x * x;
    y2 = y * y;
    iteration++;
  }
  return finalizeEscape(result, iteration, x, y, x2, y2, x0, y0, useMu);
}

// Used only for compact views around the period-3 bulbs. The historical
// iterator above stays on the overview and exterior paths.
export function iterateUntilEscapePeriodic(x0, y0, result, maxIterations = MAX_ITERATIONS, useMu = true) {
  if (isCardioidOrBulb(x0, y0)) {
    result.iterations = maxIterations;
    result.escaped = false;
    result.mu = 0;
    return result;
  }
  let x = 0, y = 0, x2 = 0, y2 = 0, iteration = 0;
  let periodicX = 0, periodicY = 0, periodicCheckpoint = 32;
  while (x2 + y2 <= 4 && iteration < maxIterations) {
    y = 2 * x * y + y0;
    x = x2 - y2 + x0;
    x2 = x * x;
    y2 = y * y;
    iteration++;
    if (iteration >= 32 && x === periodicX && y === periodicY &&
        Object.is(x, periodicX) && Object.is(y, periodicY) && x2 + y2 <= 4) {
      // An identical finite state and identical c give the same future orbit.
      // Object.is distinguishes signed zero; === excludes NaN.
      result.iterations = Math.ceil(maxIterations);
      result.escaped = false;
      result.mu = 0;
      return result;
    }
    if (iteration === periodicCheckpoint) {
      periodicX = x;
      periodicY = y;
      periodicCheckpoint *= 2;
    }
  }
  return finalizeEscape(result, iteration, x, y, x2, y2, x0, y0, useMu);
}

export function iterateUntilEscapeStripe(x0, y0, result, maxIterations = MAX_ITERATIONS, useMu = true) {
  return iterateUntilEscapePatternMode(x0, y0, result, 'stripe', maxIterations, useMu);
}

export function iterateUntilEscapePatternMode(
  x0,
  y0,
  result,
  mode = 'stripe',
  maxIterations = MAX_ITERATIONS,
  useMu = true,
) {
  if (isCardioidOrBulb(x0, y0)) {
    result.iterations = maxIterations;
    result.escaped = false;
    result.mu = 0;
    result.shade = 1;
    return result;
  }
  const highBailout = mode === 'stripe' || mode === 'binary_cells' || mode === 'normal_3d';
  const bailout = highBailout ? STRIPE_BAILOUT : ORBIT_TRAP_BAILOUT;
  const logBailout = highBailout ? LOG_STRIPE_BAILOUT : LOG_ORBIT_TRAP_BAILOUT;
  let x = 0, y = 0, x2 = 0, y2 = 0, iteration = 0;
  let dx = 0, dy = 0;
  let stripeSum = 0, prevStripe = 0, stripeCount = 0;
  let minStalk = 1e9;
  let minRing = 1e9;
  while (x2 + y2 <= bailout && iteration < maxIterations) {
    if (mode === 'normal_3d') {
      const ndx = 2 * (x * dx - y * dy) + 1;
      const ndy = 2 * (x * dy + y * dx);
      dx = ndx;
      dy = ndy;
    }
    y = 2 * x * y + y0;
    x = x2 - y2 + x0;
    x2 = x * x;
    y2 = y * y;
    iteration++;
    if (useMu && iteration >= 2) {
      if (mode === 'stripe') {
        const s = 0.5 * Math.sin(STRIPE_FREQUENCY * Math.atan2(y, x)) + 0.5;
        prevStripe = stripeSum;
        stripeSum += s;
        stripeCount++;
      } else if (mode === 'pickover_stalks') {
        const dAxis = Math.min(Math.abs(x), Math.abs(y));
        if (dAxis < minStalk) minStalk = dAxis;
      } else if (mode === 'cellular_web') {
        const dRing = Math.abs(Math.sqrt(x2 + y2) - 1.25);
        if (dRing < minRing) minRing = dRing;
      }
    }
  }
  result.iterations = iteration;
  result.escaped = x2 + y2 > bailout;
  result.mu = 0;
  result.shade = 1;
  if (result.escaped && useMu) {
    const logZn = 0.5 * Math.log(x2 + y2);
    const frac = 1 - Math.log(logZn / logBailout) * LOG2_INV;
    let mu = iteration + frac;
    let shade = 1;
    if (mode === 'stripe' && stripeCount >= 2) {
      const avg1 = stripeSum / stripeCount;
      const avg0 = prevStripe / (stripeCount - 1);
      const sa = avg0 + (avg1 - avg0) * Math.max(0, Math.min(1, frac));
      mu += 18 * sa;
      shade = 0.78 + 0.36 * sa;
    } else if (mode === 'binary_cells') {
      const ang = Math.atan2(y, x);
      const cellX = Math.sin(ang * 4);
      const cellY = Math.sin(frac * Math.PI * 2);
      const grid = cellX * cellY;
      const edge = Math.min(Math.abs(cellX), Math.abs(cellY));
      const border = Math.pow(Math.max(0, Math.min(1, edge / 0.18)), 0.45);
      if (grid > 0) mu += 6;
      shade = border * (grid > 0 ? 1.08 : 0.78);
    } else if (mode === 'normal_3d') {
      const dMag2 = dx * dx + dy * dy;
      if (dMag2 > 0) {
        const ux = (x * dx + y * dy) / dMag2;
        const uy = (y * dx - x * dy) / dMag2;
        const uMag = Math.hypot(ux, uy) || 1;
        const nx = ux / uMag;
        const ny = uy / uMag;
        const h = 1.35;
        const dot = (nx * -0.7071 + ny * -0.7071 + h) / Math.sqrt(1 + h * h);
        shade = Math.max(0.18, Math.min(1.35, 0.25 + 0.95 * Math.max(0, dot)));
      }
    } else if (mode === 'pickover_stalks') {
      const stalk = Math.exp(-minStalk * 28);
      mu += stalk * 24;
      shade = 0.72 + 0.55 * stalk;
    } else if (mode === 'cellular_web') {
      const web = Math.exp(-minRing * 22);
      mu += web * 20;
      shade = 0.70 + 0.55 * web;
    } else if (mode === 'equipotential_rings') {
      const wave = 0.5 + 0.5 * Math.cos(mu * Math.PI * 1.5);
      const ray = 0.5 + 0.5 * Math.cos(Math.atan2(y, x) * 8);
      const net = Math.max(Math.pow(wave, 6), Math.pow(ray, 8));
      mu += net * 10;
      shade = 0.72 + 0.48 * net;
    }
    result.mu = mu;
    result.shade = shade;
  }
  return result;
}
